/// <reference path="./vite.facade.d.ts" />
import { __vitePreload as preload } from "virtual:vite-preload-facade";
import { raise } from "./assertions";

interface ImportMetaWithMapDeps extends ImportMeta {
  // added to import.meta by our `vitePreloadHelpersPlugin`
  readonly mapDeps: (indices: number[]) => string[];
}

const parsedImport = <TModule>(
  url: URL,
  dependencies: string[],
  cb: () => Promise<TModule>,
  metaUrl: string,
  preloaded = false
): DynamicImport<TModule> => ({
  url,
  load: (): Promise<TModule> => (
    (preloaded = true), preload(cb, dependencies, metaUrl)
  ),
  preload: () =>
    preloaded ||
    ((preloaded = true),
    // don't pass the original callback, so the import isn't made
    preload(() => Promise.resolve(), dependencies, metaUrl)),
});

export interface DynamicImport<TModule> {
  readonly url: URL;
  load(): Promise<TModule>;
  preload(): void;
}

// vite has no API to retrieve the output path of a specific module
// without parsing the manifest file
// so we use this hacky way to extract from an import statement:
//  - the main imported url
//  - all preloaded dependencies
// see: https://github.com/vitejs/vite/issues/15431#issuecomment-1870052169
// a bootloader's rewritten import tells by itself, and reading the url
// starts the preload
type PublicParseDynamicImport = <TModule>(
  cb: () => Promise<TModule>,
  meta: ImportMeta
) => DynamicImport<TModule>;
export const parseDynamicImport: PublicParseDynamicImport = <TModule>(
  cb: () => Promise<TModule>,
  // different typing, to keep mapDeps private to this module
  { url: metaUrl, mapDeps }: ImportMetaWithMapDeps
): DynamicImport<TModule> => {
  const [url, dependencies] = parsePreload(cb, mapDeps);
  return parsedImport(new URL(url, metaUrl), dependencies, cb, metaUrl);
};

const MapDepsRegex = /__vite__mapDeps\(\[([\d,]+)\]\)/;
const ImportPathRegex = /import\((['"`])([^'"`]+)\1\)/;

// vite transforms the `() => import("./path")`
// into `() => __vitePreload(() => import("./path-[hash].js"), __vite__mapDeps([... dependencies ...]), import.meta.url)`
// with `[]` in place of the map call when the chunk has no dependency
// in dev, nothing gets rewritten
const parsePreload = (
  cb: () => Promise<unknown>,
  mapDeps: ImportMetaWithMapDeps["mapDeps"]
): [url: string, dependencies: string[]] => {
  const code = cb.toString();
  const indices = import.meta.env.DEV ? null : code.match(MapDepsRegex)?.[1];
  if (indices) {
    // the imported chunk first, as paths relative to the base
    const dependencies = mapDeps(indices.split(",").map(Number));
    return [import.meta.env.BASE_URL + dependencies[0], dependencies];
  }
  // the path as vite wrote it, relative to the importer
  return [
    code.match(ImportPathRegex)?.[2] ??
      raise(`parsePreload: no dynamic import found in function ${cb}`),
    [],
  ];
};
